// Selector caches that keep store data between calls register a reset here. Mobile resets the store
// on logout and dataset switch without restarting JS and calls resetSelectorCaches then, so caches
// keyed by the previous user's accounts, dates or states don't keep their data into the next session.
// Each reselect selector still keeps its last result until it runs again; that isn't cleared here.

const cacheResets = new Set<() => void>();
// Weak, so selectors created at runtime (and their caches) can still be collected.
const memoizedCaches = new Set<WeakRef<{ clearCache: () => void }>>();
const SWEEP_EVERY = 256;
let registrationsSinceSweep = 0;

function sweep(): void {
  memoizedCaches.forEach((ref) => {
    if (!ref.deref()) memoizedCaches.delete(ref);
  });
}

/** For module-level caches; registrations are never removed. */
export function registerCacheReset(reset: () => void): void {
  cacheResets.add(reset);
}

/** For module-level re-reselect selectors: a reset drops every key's selector. */
export function registerCachedSelectors(...selectors: Array<(...args: never[]) => unknown>): void {
  selectors.forEach((selector) => {
    // Some selectors are typed as plain functions, which hides re-reselect's clearCache.
    if ('clearCache' in selector && typeof selector.clearCache === 'function') {
      const { clearCache } = selector;
      registerCacheReset(() => clearCache());
    }
  });
}

/** For memoized functions that may be created at runtime. */
export function registerMemoizedCache(memoized: { clearCache: () => void }): void {
  memoizedCaches.add(new WeakRef(memoized));
  registrationsSinceSweep += 1;
  if (registrationsSinceSweep >= SWEEP_EVERY) {
    registrationsSinceSweep = 0;
    sweep();
  }
}

export function resetSelectorCaches(): void {
  cacheResets.forEach((reset) => reset());
  memoizedCaches.forEach((ref) => ref.deref()?.clearCache());
  sweep();
}
